/**
 * Renders all order-level modals (ApproveEvbv, BoValues, ImageViewer,
 * Review, DocViewer, Site NPI, auth letter, Candid RPA status) and
 * manages their open/close lifecycle via Redux and local state.
 */
import React, { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { openModal } from "risa-oasis-ui_v2";
import ApproveEvbv from "../../../components/modals/approveEvbv/approveEvbv";
// import BoValuesModal from "../../../components/modals/BoValuesModal/BoValuesModal";
import AddSiteNpiModal from "../../../components/modals/addSiteNpiModal/addSiteNpiModal";
import { getSiteDetailsInitialFormValues } from "../../../components/modals/addSiteNpiModal/siteDetailsForm.types";
import AuthLetterSuccessModal from "../../../components/modals/authLetterSuccessModal/authLetterSuccessModal";
import AuthLetterUploadModal from "../../../components/modals/authLetterUploadModal/authLetterUploadModal";
import CandidRpaStatusModal from "../../../components/modals/candidRpaStatus/candidRpaStatusModal";
import DocViewerModal from "../../../components/modals/docViewerModal/docViewerModal";
import ImageviewerModal from "../../../components/modals/imageViewerModal/imageviewerModal";
import ImageViewerTimestamp from "../../../components/modals/imageViewerModal/imageViewerTimestamp";
import NewDocumentUpload from "../../../components/modals/newDocumentUpload/newDocumentUpload";
import PatientNotesModal from "../../../components/modals/patientNotes/patientNotesModal";
import ReviewModal from "../../../components/modals/ReviewModal/ReviewModal";
import { useDataAccumulatorForSingleOrder } from "../../../context/dataAccumulatorForSingleOrder";
import { AuthStatusOptionModel } from "../../../data-model/authStatusOptions";
import { BadgeWithIconProps } from "../../../data-model/badgeWithIconProps";
import {
  AuthOnFile,
  GeneralOrderDetails,
} from "../../../data-model/medicalPaOrdersModel";
import { financialStatusOptions } from "../../../enums/medicalPaOrderStatus";
import { ModalId } from "../../../enums/modalId";
import { closeModal } from "../../../redux/slice/modalSlice";
import { AppDispatch } from "../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { checkIfSubmissionPageShouldBeShown } from "../utils/orderDataHelpers";

/** Props for the OrderModals component. */
interface OrderModalsProps {
  isApproveEvbvModalOpen: boolean;
  setIsApproveEvbvModalOpen: (open: boolean) => void;
  openedModalId: string | null;
  imageViewerModalOpen: boolean;
  setImageViewerModalOpen: (open: boolean) => void;
  // singleMedicalPaOrder: MedicalPaOrder | null | undefined;
  generalOrderDetails: GeneralOrderDetails | null | undefined;
  authOnFileDetails: AuthOnFile | null | undefined;
  currentStatusBadge: BadgeWithIconProps | undefined;
  authStatusOptions: AuthStatusOptionModel[] | undefined;
  userEmail: string;
}

/**
 * Conditionally renders order-level modals based on the currently opened
 * modal ID and local boolean flags, including Candid RPA status.
 *
 * @param props - Modal open states, order data, status badge, and user email
 * @returns The conditionally rendered modal elements
 */
const OrderModals: React.FC<OrderModalsProps> = ({
  isApproveEvbvModalOpen,
  setIsApproveEvbvModalOpen,
  openedModalId,
  imageViewerModalOpen,
  setImageViewerModalOpen,
  // singleMedicalPaOrder,
  generalOrderDetails,
  authOnFileDetails,
  currentStatusBadge,
  authStatusOptions,
  userEmail,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { submissionData, fetchGeneralOrderDetails } =
    useDataAccumulatorForSingleOrder();
  /**
   * Maps submission / auth-on-file data into Site NPI form defaults.
   * Memoized so parent re-renders do not change object identity and wipe
   * in-progress edits inside AddSiteNpiModal's initialValues effect.
   */
  const siteNpiInitialValues = useMemo(
    () =>
      getSiteDetailsInitialFormValues(
        submissionData?.submission?.test_location_submission_details,
        authOnFileDetails ?? undefined,
      ),
    [
      submissionData?.submission?.test_location_submission_details,
      authOnFileDetails,
    ],
  );

  useEffect(() => {
    if (isApproveEvbvModalOpen) {
      openModal("approve-evbv-modal");
    }
  }, [isApproveEvbvModalOpen]);

  useEffect(() => {
    if (openedModalId === ModalId.BO_VALUES_MODAL) {
      openModal(ModalId.BO_VALUES_MODAL);
    }
    if (openedModalId === ModalId.REVIEW_MODAL) {
      openModal(ModalId.REVIEW_MODAL);
    }
    if (openedModalId === ModalId.DOC_VIEWER_MODAL) {
      openModal(ModalId.DOC_VIEWER_MODAL);
    }
    if (openedModalId === ModalId.AUTH_LETTER_UPLOAD_MODAL) {
      openModal(ModalId.AUTH_LETTER_UPLOAD_MODAL);
    }
    if (openedModalId === ModalId.AUTH_LETTER_SUCCESS_MODAL) {
      openModal(ModalId.AUTH_LETTER_SUCCESS_MODAL);
    }
    if (openedModalId === ModalId.ADD_SITE_NPI_MODAL) {
      openModal(ModalId.ADD_SITE_NPI_MODAL);
    }
    if (openedModalId === ModalId.NEW_DOCUMENT_UPLOAD_MODAL) {
      openModal(ModalId.NEW_DOCUMENT_UPLOAD_MODAL);
    }
    if (openedModalId === ModalId.PATIENT_NOTES) {
      openModal(ModalId.PATIENT_NOTES);
    }
    if (openedModalId === ModalId.CANDID_RPA_STATUS_MODAL) {
      openModal(ModalId.CANDID_RPA_STATUS_MODAL);
    }
  }, [openedModalId]);

  useEffect(() => {
    if (imageViewerModalOpen) {
      openModal("image-viewer-modal");
    }
  }, [imageViewerModalOpen]);

  const handleCloseImageViewerModal = () => {
    setImageViewerModalOpen(false);
    dispatch(closeModal());
  };

  const handleCloseReviewModal = () => {
    dispatch(closeModal());
  };

  const handleReviewModalSubmit = () => {
    dispatch(closeModal());
  };

  return (
    <>
      {isApproveEvbvModalOpen && (
        <ApproveEvbv
          onClose={() => setIsApproveEvbvModalOpen(false)}
          badgeList={financialStatusOptions.sort((a, b) =>
            a.id.localeCompare(b.id),
          )}
          selectedBadge={currentStatusBadge}
          orderId={generalOrderDetails?.order_id ?? ""}
          shouldNavigateToSubmission={checkIfSubmissionPageShouldBeShown(
            generalOrderDetails,
            authOnFileDetails,
            authStatusOptions,
          )}
        />
      )}

      {/* {openedModalId === ModalId.BO_VALUES_MODAL && (
        <BoValuesModal onClose={handleCloseBoValuesModal} />
      )} */}

      {imageViewerModalOpen && (
        <ImageviewerModal
          fileUrls={[]}
          onClose={handleCloseImageViewerModal}
          filePaths={
            authOnFileDetails?.screenshots?.treatment_plan_path
              ? [authOnFileDetails?.screenshots?.treatment_plan_path]
              : []
          }
        >
          <ImageViewerTimestamp createdAt={authOnFileDetails?.created_at} />
        </ImageviewerModal>
      )}

      {openedModalId === ModalId.REVIEW_MODAL && (
        <ReviewModal
          onClose={handleCloseReviewModal}
          orderId={generalOrderDetails?.order_id ?? ""}
          orgId={getOrgIdForFetchExternalWorklist() ?? ""}
          patientId={generalOrderDetails?.demographics?.patient_id ?? ""}
          reviewedBy={userEmail}
          masterAuthStatus={generalOrderDetails?.status?.master_auth_status}
          onSubmit={handleReviewModalSubmit}
        />
      )}

      {/*
       * Doc viewer is launched from the regimen info bar's
       * "View Order Document" action (radiology orgs only). The file path
       * and title are supplied via the modal's metaData in Redux.
       */}
      {openedModalId === ModalId.DOC_VIEWER_MODAL && (
        <DocViewerModal
          onClose={() => dispatch(closeModal())}
          type={"clinical_attachment"}
        />
      )}

      {openedModalId === ModalId.AUTH_LETTER_UPLOAD_MODAL && (
        <AuthLetterUploadModal onClose={() => dispatch(closeModal())} />
      )}

      {openedModalId === ModalId.AUTH_LETTER_SUCCESS_MODAL && (
        <AuthLetterSuccessModal onClose={() => dispatch(closeModal())} />
      )}

      {openedModalId === ModalId.ADD_SITE_NPI_MODAL && (
        <AddSiteNpiModal
          onClose={() => dispatch(closeModal())}
          initialValues={siteNpiInitialValues}
          fetchGeneralOrderDetails={fetchGeneralOrderDetails}
          generalOrderDetails={generalOrderDetails ?? undefined}
        />
      )}
      {openedModalId === ModalId.NEW_DOCUMENT_UPLOAD_MODAL && (
        <NewDocumentUpload onClose={() => dispatch(closeModal())} />
      )}
      {openedModalId === ModalId.PATIENT_NOTES && (
        <PatientNotesModal onClose={() => dispatch(closeModal())} />
      )}
      {openedModalId === ModalId.CANDID_RPA_STATUS_MODAL && (
        <CandidRpaStatusModal onClose={() => dispatch(closeModal())} />
      )}
    </>
  );
};

export default OrderModals;
